import { useQueryClient } from '@tanstack/react-query';
import {
  CHAT_CONVERSATION_PARAM,
  ConversationSupportAudience,
  ConversationType,
  handleSetUrlSearchParams,
  queryKeys,
} from '@vgrubs/components';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The three places the inbox can be.
 *
 * Not a filter - a place. Answering a merchant, answering a sales agent and
 * talking to the person at the next desk are three jobs with three different
 * etiquettes, and mixing them in one list makes every glance at it a sorting
 * exercise. Which one you are in also decides who the "+" can even offer, so
 * this is what that modal follows.
 *
 * The first two are one conversation type (`support`) split by the role of the
 * outside user, which the backend exposes as `supportAudience`; the third is
 * two types at once (`direct` and `group`) and no audience at all.
 */
export type ChatScope =
  | 'merchant'
  | 'sales'
  | 'internal'
  /* Customers writing from a vDrive tracking link. */
  | 'dispatcher';
/* The website chat is off for now (docs/dispatcher-chat-front.md); its tab
   comes back with a 'website' scope here and in SCOPE_QUERY:
  | 'website'; */

const SCOPES: ChatScope[] = ['merchant', 'sales', 'internal', 'dispatcher'];

/** What each tab asks the inbox endpoint for. Never both at once. */
const SCOPE_QUERY: Record<
  ChatScope,
  { types?: ConversationType[]; supportAudience?: ConversationSupportAudience }
> = {
  merchant: { supportAudience: 'merchant' },
  sales: { supportAudience: 'sales' },
  internal: { types: ['direct', 'group'] },
  dispatcher: { types: ['dispatcher'] },
  // website: { types: ['website'] },
};

/**
 * What the inbox is showing, and which thread is open.
 *
 * The scope, the search and the open conversation live in the URL so the view
 * survives a reload and can be linked to; unanswered and unread are local,
 * since they are a moment's narrowing rather than a place.
 *
 * The open thread is a search param rather than a route: both routers key
 * `<Routes>` on `location.pathname` inside `AnimatePresence mode="wait"`, so a
 * path change would remount and refade the whole page - including the inbox -
 * on every thread click.
 */
export const useChatFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  /*
   * Read, not held. As state seeded once from the URL it could not be steered
   * from outside the page - the mention toast links straight to an internal
   * thread, and arriving here already mounted would have opened it while the
   * list beside it stayed on the merchant queue.
   *
   * Merchant is the default and therefore the ABSENCE of the param: one URL for
   * the page people land on, and every ?scope=internal link already sent around
   * still opens the tab it names.
   */
  const scopeParam = searchParams.get('scope');
  const scope: ChatScope =
    scopeParam && SCOPES.includes(scopeParam as ChatScope)
      ? (scopeParam as ChatScope)
      : 'merchant';
  const [query, setQuery] = useState(searchParams.get('query') || '');
  /* Local, not in the URL: these are a moment's narrowing of the list rather
     than a place you would link somebody to. */
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  /* The shelf, alongside the other two rather than instead of them: it holds
     both halves of the inbox, so it narrows whichever one you are on. */
  const [archived, setArchived] = useState(false);
  /* Merchants tab only: the merchant groups without the support threads they
     are listed beside. */
  const [groupsOnly, setGroupsOnly] = useState(false);

  /*
   * Waiting and Unread are one choice, not two switches.
   *
   * Both ask the same question - which of these still needs somebody - and
   * their intersection is a handful of threads at best, so the two on together
   * mostly produced an empty list that reads as a broken filter rather than as
   * an answer. Turning one on lets the other go; turning it off leaves you back
   * on All, which is what the chip row already shows.
   *
   * Archive is not in this pair: it is the shelf rather than a question about
   * the inbox, so it narrows whichever of the two you are on.
   */
  const chooseUnanswered = (value: boolean) => {
    setUnansweredOnly(value);
    if (value) {
      setUnreadOnly(false);
      setGroupsOnly(false);
    }
  };

  /*
   * Groups and Waiting exclude each other the same way: only a support thread
   * waits on an answer, so the two together could only ever be empty. Unread
   * and Archived narrow the groups like they narrow everything else.
   */
  const chooseGroups = (value: boolean) => {
    setGroupsOnly(value);
    if (value) setUnansweredOnly(false);
  };

  const chooseUnread = (value: boolean) => {
    setUnreadOnly(value);
    if (value) setUnansweredOnly(false);
  };

  const selectedParam = searchParams.get(CHAT_CONVERSATION_PARAM);
  const selectedId = selectedParam ? Number(selectedParam) : null;

  const selectThread = (conversationId: number | null) =>
    handleSetUrlSearchParams(
      CHAT_CONVERSATION_PARAM,
      conversationId === null ? '' : String(conversationId),
      setSearchParams
    );

  const changeQuery = (next: string) => {
    setQuery(next);
    handleSetUrlSearchParams('query', next, setSearchParams);
  };

  /** Switching tabs closes the open thread - it is not in the new list. */
  const changeScope = (next: ChatScope) => {
    /*
     * Drop the lists nobody is looking at before this one mounts.
     *
     * A message for another tab is deliberately not applied to that tab's
     * cached list and not flagged as stale - only its badge moves (see
     * `belongsToScope` in `useConversationsSocket`). So the list behind a
     * lit badge can predate its own traffic, and React Query would happily
     * serve that cache for its staleTime: badge says one unread, list shows
     * none. Removing inactive caches makes every tab arrival fetch page 0
     * fresh - a remove rather than a reset, so the list being LEFT does not
     * refetch on its way out.
     */
    queryClient.removeQueries({
      queryKey: queryKeys.conversations.inboxAll,
      type: 'inactive',
    });
    handleSetUrlSearchParams(
      'scope',
      next === 'merchant' ? '' : next,
      setSearchParams
    );
    selectThread(null);
    /* The filters belong to the list you were looking at: "waiting" does not
       exist on the internal tab at all, and an unread filter carried across
       reads as an empty inbox on the tab you just opened. */
    setUnansweredOnly(false);
    setUnreadOnly(false);
    setArchived(false);
    setGroupsOnly(false);
  };

  /*
   * The groups-only list asks for the type instead of the audience - the
   * audience is what pulls the support threads in beside the groups. It is
   * also a scope of its own to the socket, which keys "does this row belong
   * on screen" on the same string the query does.
   */
  const scopeQuery =
    scope === 'merchant' && groupsOnly
      ? { types: ['merchantGroup' as ConversationType] }
      : SCOPE_QUERY[scope];

  return {
    filters: {
      scope,
      ...scopeQuery,
      groupsOnly,
      archived,
      query,
      unansweredOnly,
      unreadOnly,
    },
    selectedId,
    selectThread,
    changeQuery,
    changeScope,
    /* The pair above, not the raw setters: the "one at a time" rule belongs
       with the state rather than with whoever draws the chips. */
    setUnansweredOnly: chooseUnanswered,
    setUnreadOnly: chooseUnread,
    setArchived,
    setGroupsOnly: chooseGroups,
  };
};
